import { inject } from "inversify";
import { ICookieService, ICookieServiceId } from "../services/interfaces/cookieService";
import { ModalComponent } from "./modalComponent";
import { IMessagingService } from "../services";
import { CookieCategory } from "../enums/cookieCategory"
import { CookieCategoryPermissions } from "../models/cookieCategoryPermission"
import { getCookie, setCookie } from "../utils";
import { ILoggingService } from "../services/interfaces/loggingService";

class CookieModalComponent extends ModalComponent {

    private gTagFunction: Gtag.Gtag;

    private divCookiePolicy: HTMLDivElement;
    private divDefaultDisplay: HTMLDivElement;
    private divSettings: HTMLDivElement;

    private checkboxFunctional: HTMLInputElement;
    private checkboxMarketing: HTMLInputElement;
    private checkboxPerformance: HTMLInputElement

    private onClickApproveAllCookies: EventListener;
    private onClickRejectOptionalCookies: EventListener;
    private onClickShowCookiePolicyLink: EventListener;
    private onClickEditCookieSettings: EventListener;
    private onClickBackLink: EventListener;
    private onClickSaveEditedCookieSettings: EventListener;
    private onInputSwitchChanged: EventListener;
    private onInputSwitchKeyDown: EventListener;
    
    private backLinkTarget: string;

    private forceModalToShow: boolean = false;

    private visitorsPriorCookieSelections: CookieCategoryPermissions = null;


    constructor(componentEl: HTMLElement,
        messagingService: IMessagingService,
        protected cookieService: ICookieService,
        protected appInsightsWrapper: ILoggingService) {

        super(componentEl, messagingService); //calling this triggers other override implementations of ModalComponent in this class 

    }
    private initCookieModal() {
        //because showModal gets called async from the super, this needs to be called before showModal does any substantive processing
        super.isCookieModal = true;

        //bind the listener to a handler to do something
        this.onClickApproveAllCookies = this.onClickApproveAllCookiesHandler.bind(this);
        this.onClickRejectOptionalCookies = this.onClickRejectOptionalCookiesHandler.bind(this);
        this.onClickShowCookiePolicyLink = this.onClickShowCookiePolicyLinkHandler.bind(this);
        this.onClickEditCookieSettings = this.onClickEditCookieSettingsHandler.bind(this);
        this.onClickBackLink = this.onClickBackLinkHandler.bind(this);
        this.onClickSaveEditedCookieSettings = this.onClickSaveEditedCookieSettingsHandler.bind(this);
        this.onInputSwitchChanged = this.onInputSwitchChangedHandler.bind(this);
        this.onInputSwitchKeyDown = this.onInputSwitchKeyDownHandler.bind(this);

        this.divCookiePolicy = this.modal.querySelector<HTMLDivElement>('.cookiemodal__policy');
        this.divDefaultDisplay = this.modal.querySelector<HTMLDivElement>('.cookiemodal__defaultdisplay');
        this.divSettings = this.modal.querySelector<HTMLDivElement>('.cookiemodal__settings');

        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__editsettings').forEach(btn => btn.addEventListener("click", this.onClickEditCookieSettings));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__approveall').forEach(btn => btn.addEventListener("click", this.onClickApproveAllCookies));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__rejectoptional').forEach(btn => btn.addEventListener("click", this.onClickRejectOptionalCookies));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__showpolicy').forEach(btn => btn.addEventListener("click", this.onClickShowCookiePolicyLink));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__back').forEach(btn => btn.addEventListener("click", this.onClickBackLink));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__savesettings').forEach(btn => btn.addEventListener("click", this.onClickSaveEditedCookieSettings));
        this.modal.querySelectorAll<HTMLInputElement>('input.switch').forEach(btn => {
            btn.addEventListener("change", this.onInputSwitchChanged);
            btn.addEventListener("keydown", this.onInputSwitchKeyDown);
        });

        this.modal.querySelectorAll(".modal__btn-close").forEach((element: HTMLElement) => {
            element.style.display = "none";
        });
        this.modal.querySelectorAll(".modal__footer").forEach((element: HTMLElement) => {
            element.style.display = "none";
        });

        this.checkboxFunctional = this.modal.querySelector<HTMLInputElement>('#cookies-functional');
        this.checkboxMarketing = this.modal.querySelector<HTMLInputElement>('#cookies-marketing');
        this.checkboxPerformance = this.modal.querySelector<HTMLInputElement>('#cookies-performance');

        this.modal.querySelector<HTMLInputElement>('#cookies-strictlynecessary').checked = true;
        this.modal.querySelector<HTMLInputElement>('#cookies-strictlynecessary').disabled = true;
        this.checkboxMarketing.checked = false;
        this.checkboxPerformance.checked = false;
        this.checkboxFunctional.checked = false;
    }

    private overrideModalSettings() {

        this.modal.style.maxHeight = "inherit";
        this.modal.style.overflowY = "scroll";
        this.modal.style.zIndex = "1100";
        this.backdrop.style.zIndex = "1099";
    }

    protected async init() {
        this.initCookieModal();
        super.init();

    }

    protected onKeydownHandler(e) {
        super.onKeydownHandler(e);
        if (this.modal.contains(e.target) && e.which === 27) {
            if (this.visitorsPriorCookieSelections == null) {
                //no cookie setting have been saved so make Escape the same as accept all 
                this.onClickSaveEditedCookieSettingsHandler(e);
            }
            else {
                // just retain existing settings
                this.hideModal();
            }
        }
    }

    //this will get called from the super's init method
    public showModal() {
        this.cookieService.GetConsentCookiePermissions().then(cookieData => {
            if (cookieData.content != null) {
                this.visitorsPriorCookieSelections = JSON.parse(cookieData.content);
            }
            if (this.visitorsPriorCookieSelections != null && this.forceModalToShow == true) {
                //the visitor has preference and the modal is being force shown, setup the UI
                this.checkboxMarketing.checked = this.visitorsPriorCookieSelections.Marketing;
                this.checkboxPerformance.checked = this.visitorsPriorCookieSelections.Performance;
                this.checkboxFunctional.checked = this.visitorsPriorCookieSelections.Functional;
                //simulate some clicks to get the label text to match the button state
                this.UpdateButtonLabelToMatchToggleState(this.checkboxMarketing);
                this.UpdateButtonLabelToMatchToggleState(this.checkboxPerformance);
                this.UpdateButtonLabelToMatchToggleState(this.checkboxFunctional);
            }
            if (this.visitorsPriorCookieSelections == null || this.forceModalToShow == true) {
                //the visitor has no preferences or the modal is being force shown, show the UI
                super.showModal();
                this.overrideModalSettings();
            }
            else {
                //if the cookieModal decided it did not need to show itself on screen, then cleanup its handlers
                //let the App Insights know what it can track
                if (this.visitorsPriorCookieSelections != null && this.visitorsPriorCookieSelections.Performance == true) {
                    this.appInsightsWrapper.EnableTrackingCookies();
                }
                this.hideModal();
            }
        });

        return this;
    }

    public initView() {
        this.header.classList.remove('hidden');
        this.divDefaultDisplay.classList.remove('hidden');
        this.divCookiePolicy.classList.add('hidden');
        this.divSettings.classList.add('hidden');
    }

    private onClickApproveAllCookiesHandler(e) {

        //set the cookies server side with a service
        this.cookieService.SaveConsentCookie(true, true, true);

        this.hideModal();
    }

    private onClickRejectOptionalCookiesHandler(e) {

        //set the cookies server side with a service
        this.cookieService.SaveConsentCookie(false, false, false);

        this.hideModal();
    }

    private onClickShowCookiePolicyLinkHandler(e) {
        e.preventDefault();
        this.backLinkTarget = e.target.dataset.source;
        this.header.classList.add('hidden');
        this.divDefaultDisplay.classList.add('hidden');
        this.divSettings.classList.add('hidden');
        this.divCookiePolicy.classList.remove('hidden');
        //we have changed focusable elements on the screen, update the list
        this.UpdateFocusableElements();
        this.firstFocusTargetEl.focus();
    }
    private onClickEditCookieSettingsHandler(e) {
        this.header.classList.add('hidden');
        this.divDefaultDisplay.classList.add('hidden');
        this.divCookiePolicy.classList.add('hidden');
        this.divSettings.classList.remove('hidden');

        //we have changed focusable elements on the screen, update the list
        this.UpdateFocusableElements();
        this.firstFocusTargetEl.focus();
        this.firstFocusTargetEl.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
    private onClickBackLinkHandler(e) {
        this.backLinkTarget === "edit" ? this.onClickEditCookieSettingsHandler(e) : this.initView();
        this.UpdateFocusableElements();
        this.firstFocusTargetEl.focus();
    }
    private onClickSaveEditedCookieSettingsHandler(e) {

        //set the cookies server side with a service
        this.cookieService.SaveConsentCookie(this.checkboxMarketing.checked, this.checkboxFunctional.checked, this.checkboxPerformance.checked);

        this.hideModal();
    }

    private onInputSwitchKeyDownHandler(e) {
        let el: HTMLInputElement = e.target;
        if (e.key === 'ArrowLeft' && el.checked || e.key === 'ArrowRight' && !el.checked) {
            el.click();
        }
    }

    private onInputSwitchChangedHandler(e) {
        let el: HTMLInputElement = e.target;
        this.UpdateButtonLabelToMatchToggleState(el);
    }

    private UpdateButtonLabelToMatchToggleState(el: HTMLInputElement) {
        el.parentElement.querySelector<HTMLSpanElement>('.checkedlabel').innerText = (el.checked == true ? 'On' : 'Off');
    }

    public ShowModalInEditMode() {
        this.forceModalToShow = true;
        this.showModal();
    }

    public hideModal() {
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__editsettings').forEach(btn => btn.removeEventListener("click", this.onClickEditCookieSettings));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__approveall').forEach(btn => btn.removeEventListener("click", this.onClickApproveAllCookies));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__rejectoptional').forEach(btn => btn.removeEventListener("click", this.onClickRejectOptionalCookies));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__showpolicy').forEach(btn => btn.removeEventListener("click", this.onClickShowCookiePolicyLink));
        this.modal.querySelectorAll<HTMLButtonElement>('.cookiemodal__savesettings').forEach(btn => btn.removeEventListener("click", this.onClickSaveEditedCookieSettings));
        this.modal.querySelectorAll<HTMLInputElement>('input.switch').forEach(btn => {
            btn.removeEventListener("change", this.onInputSwitchChanged);
            btn.removeEventListener("keydown", this.onInputSwitchKeyDown);
        });
        super.hideModal();
        return this;
    }
}

export { CookieModalComponent };
